import { OFFICE_HEADER } from '@velora/contracts';
import { COOKIE_CSRF, CSRF_HEADER } from '@velora/contracts/identity';
import { createHttpClient, type HttpClient } from '@velora/sdk';
import { readCookie } from '../modules/identity/index.js';
import { demoFetch, isDemoMode } from './demo/mode.js';

/**
 * Browser clients for the Velora API; `/v1` is proxied to apps/api in dev.
 *
 * Every request carries the CSRF token from the `velora_csrf` cookie (read
 * per request, so it is always the current one); without it the identity
 * auth provider refuses POST/PUT/PATCH/DELETE with 403 `csrf`.
 *
 * `api` carries no office selector value (practice-wide routes). Office
 * scoped requests go through `clientFor(office)`, which pins the
 * `x-velora-office` header to the value the query was keyed on, so a
 * response can never be cached under another office's key.
 */
/** Resolves `fetch` per call (not at import), so tests and polyfills can replace it. */
const lateFetch: typeof fetch = (input, init) => globalThis.fetch(input, init);
const fetchImpl: typeof fetch = isDemoMode() ? demoFetch : lateFetch;

export function csrfHeaders(): Record<string, string> {
  const token = readCookie(COOKIE_CSRF);
  return token ? { [CSRF_HEADER]: token } : {};
}

export const api: HttpClient = createHttpClient({
  baseUrl: '/v1',
  headers: csrfHeaders,
  fetch: fetchImpl,
});

const scoped = new Map<string, HttpClient>();

/**
 * The office selector value in the address bar (`?office=`, the selector's
 * source of truth), or `null` before the shell has filled it in.
 */
export function currentOfficeParam(): string | null {
  if (typeof window === 'undefined') return null;
  return new URLSearchParams(window.location.search).get('office');
}

/**
 * A client carrying the office selected right now, for practice-wide routes
 * that still follow the selector (patient search: one office selected lists
 * only that office's patients). Inside React prefer `clientFor(param)` with
 * `useOfficeScope().param` in the query key.
 */
export function currentOfficeClient(): HttpClient {
  const office = currentOfficeParam();
  return office ? clientFor(office) : api;
}

export function clientFor(office: string): HttpClient {
  let client = scoped.get(office);
  if (!client) {
    client = createHttpClient({
      baseUrl: '/v1',
      headers: () => ({ ...csrfHeaders(), [OFFICE_HEADER]: office }),
      fetch: fetchImpl,
    });
    scoped.set(office, client);
  }
  return client;
}
